import React, { FC, useCallback } from 'react';
import { connect } from 'react-redux';

import type { AmountInputProps } from '../../../../types';
import type { FreeBet } from 'app/api/services/freebets/types';
import type { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { Amount } from 'app/design-system/components/Amount';
import { Trans } from 'app/lang';
import { StakeType } from 'app/lib/staking/constants';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';

import { RemoveFreeBet, removeFreeBet } from '../../../../actions/paymentOptions';
import { RemoveExpressFreeBet, removeExpressFreeBet } from '../../../../actions/stakeTypes/express';
import { AccountType } from '../../../../constants';
import { selectCurrentStakeType } from '../../../../selectors/stakeTypes';
import SelectedPaymentOption from '../SelectedPaymentOption';

import dictionary from './FreeBetAccount.dictionary.json';

import styles from './FreeBetAccount.module.scss';

// ---

type StoreProps = {
  loggedIn: boolean;
  isExpress: boolean;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<RemoveFreeBet | RemoveExpressFreeBet>;
};

type FreeBetAccountProps = {
  value?: FreeBet | null;
  betId?: number;
  inputProps: AmountInputProps;
};

const FreeBetAccount: FC<FreeBetAccountProps & DispatchProp & StoreProps> = ({
  betId,
  value,
  inputProps,
  isExpress,
  dispatch,
}) => {
  const removeFreeBetHandler = useCallback(() => {
    const { onChange, name } = inputProps;

    onChange({ value: '', name, account: AccountType.MAIN_ACCOUNT });

    if (isExpress) {
      dispatch(removeExpressFreeBet());
    }
    else {
      if (!betId) {
        console.error(`removeFreeBetHandler -> betId not found = ${betId}`);
        return;
      }

      dispatch(removeFreeBet(betId));
    }
  }, [inputProps, betId]);

  const label = (
    <div data-test="freeBetAccount-label">
      <Trans data={dictionary.label}
             components={{
               amount: (
                 value ? (
                   <Amount value={value.amount}
                           className={styles.amount}
                           currency={value.currency} />
                 ) : <>{'--'}</>
               ),
             }} />
    </div>
  );

  return (
    <div data-test="freeBetAccount-container">
      <SelectedPaymentOption label={label}
                             onClick={removeFreeBetHandler} />
    </div>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  loggedIn: selectIsLoggedIn(state),
  isExpress: selectCurrentStakeType(state).has(StakeType.EXPRESS),
});

export default connect(mapStateToProps)(FreeBetAccount);
